import { useEffect, useState } from "react";
import Select from "react-select";
import SimpleModalFooter from "app/components/Modals/SimpleModalFooter";
import { selectAllCompanyUsersById } from "app/containers/Global/slice";
import { useAppSelector } from "utils/redux/hooks";
import { ManagerScope } from "../types";

interface Props {
  onBack: () => void;
  onSave: (selectedManagerIds: number[], managerScope: ManagerScope) => void;
  preselectedUserIds?: number[];
  managerScope?: ManagerScope;
  getWarningBanner?: (sendToType: string) => JSX.Element | null;
}

const SCOPE_OPTIONS: { value: ManagerScope; label: string; hint: string }[] = [
  {
    value: "directReports",
    label: "All Direct Reports",
    hint: "Only the people who report to them directly.",
  },
  {
    value: "allSubordinates",
    label: "All Subordinates",
    hint: "Everyone beneath them in the org chart, at every level.",
  },
];

const ManagerEmailConfig = ({
  onBack,
  onSave,
  preselectedUserIds,
  managerScope,
  getWarningBanner,
}: Props) => {
  const usersById = useAppSelector(selectAllCompanyUsersById);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [selectedScope, setSelectedScope] =
    useState<ManagerScope>("directReports");

  useEffect(() => {
    if (preselectedUserIds) {
      setSelectedIds(preselectedUserIds);
    }
  }, [preselectedUserIds]);

  useEffect(() => {
    if (managerScope) {
      setSelectedScope(managerScope);
    }
  }, [managerScope]);

  const getUserOption = (userAccountId: number | string) => {
    const user = usersById[userAccountId];
    const name =
      `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim() ||
      user?.emailAddress;

    return {
      label: name ?? "",
      value: `${userAccountId}`,
    };
  };

  const getScopeToggle = ({ value, label, hint }: (typeof SCOPE_OPTIONS)[0]) => {
    const isSelected = selectedScope === value;

    return (
      <button
        key={value}
        type="button"
        aria-pressed={isSelected}
        onClick={() => setSelectedScope(value)}
        className={`flex flex-1 flex-col gap-1 rounded-lg border p-3 text-start ${
          isSelected
            ? "border-primary bg-primary-05"
            : "border-charcoal-20 bg-white"
        }`}
      >
        <span className="text-sm font-semibold">{label}</span>
        <span className="text-xs text-charcoal-80">{hint}</span>
      </button>
    );
  };

  return (
    <>
      <Select
        noOptionsMessage={() => null}
        placeholder="Search by manager name or email..."
        options={Object.keys(usersById).map(getUserOption)}
        isMulti
        value={selectedIds.map(getUserOption)}
        onChange={(values) => {
          if (values) {
            setSelectedIds(values.map(({ value }) => Number(value)));
          }
        }}
        classNames={{
          multiValue: () => "select__multi-value",
        }}
        filterOption={(option, inputValue) => {
          const user = usersById[option.data.value];
          const name = `${user?.firstName ?? ""} ${
            user?.lastName ?? ""
          }`.trim();
          const email = user?.emailAddress ?? "";
          return (
            name.toLowerCase().includes(inputValue.toLowerCase()) ||
            email.toLowerCase().includes(inputValue.toLowerCase())
          );
        }}
      />
      <div className="column-gap-8px">
        <p className="navy-text">
          For the managers selected above, send this email to:
        </p>
        <div className="flex gap-2">{SCOPE_OPTIONS.map(getScopeToggle)}</div>
      </div>
      {selectedIds.length && getWarningBanner
        ? getWarningBanner("managers")
        : null}
      <SimpleModalFooter
        onBackButtonClick={onBack}
        onPrimaryButtonClick={() => onSave(selectedIds, selectedScope)}
        isPrimaryButtonDisabled={!selectedIds.length}
        primaryButtonText="Next"
      />
    </>
  );
};

export default ManagerEmailConfig;
